import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useCallback, type MutableRefObject } from 'react'
import { isNavSubActive } from '@/utils/navRoutes'
import type { NavItem } from './Sidebar'

/**
 * Enlace del rail colapsado sobre el que está el puntero, junto al rectángulo
 * capturado en el mouseenter. Se usa para anclar el overlay fuera del nav
 * scrolleable, porque el `overflow-y-auto` del rail recorta los tooltips y los
 * flyouts que quedan fuera de su caja.
 */
export interface RailHover {
  item: NavItem
  rect: Pick<DOMRect, 'top' | 'right' | 'height'>
}

interface SidebarRailPanelProps {
  hover: RailHover
  pathname: string
  onNavigate: () => void
}

/**
 * Contenido puro del overlay (sin portal) para poder probarlo con
 * renderToStaticMarkup: tooltip simple para enlaces sin submenú y panel
 * flyout para los grupos (Catálogo, Agente IA/Bot).
 */
export function SidebarRailPanel({ hover, pathname, onNavigate }: SidebarRailPanelProps) {
  const { item, rect } = hover
  const subItems = item.subItems ?? []

  if (subItems.length > 0) {
    // Se limita al viewport para que el flyout de un ítem al final del rail
    // no se salga de la pantalla.
    const maxTop = typeof window !== 'undefined' ? window.innerHeight - 320 : rect.top
    const top = Math.max(8, Math.min(rect.top, maxTop))

    return (
      <div
        data-testid="sidebar-rail-flyout"
        style={{ top, left: rect.right + 8 }}
        className="fixed z-[70] w-52 rounded-2xl border border-white/10 bg-slate-900/95 p-2 shadow-2xl backdrop-blur"
      >
        <div className="mb-1 border-b border-white/10 px-3 py-1.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">{item.name}</p>
        </div>
        <div className="space-y-0.5">
          {subItems.map((sub) => (
            <Link
              key={sub.path}
              to={sub.path}
              onClick={onNavigate}
              className={[
                'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-xs transition-colors',
                isNavSubActive(pathname, sub.path)
                  ? 'bg-white/20 font-semibold text-white'
                  : 'text-white/75 hover:bg-white/10 hover:text-white',
              ].join(' ')}
            >
              <i className={`${sub.icon} w-3.5 text-center text-xs`} />
              <span className="truncate">{sub.name}</span>
            </Link>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="sidebar-rail-tooltip"
      style={{ top: rect.top + rect.height / 2, left: rect.right + 8 }}
      className="fixed z-[70] -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-800 px-2.5 py-1.5 text-xs text-white shadow-xl"
    >
      {item.name}
    </div>
  )
}

interface SidebarRailOverlayProps {
  hover: RailHover
  pathname: string
  onNavigate: () => void
  onClose: () => void
  /** Nodo del panel para que el rail sepa si el puntero sigue dentro al salir del enlace. */
  panelRef: MutableRefObject<HTMLDivElement | null>
}

/**
 * Portala el overlay del rail a `document.body` para que el `overflow-y-auto`
 * del nav nunca lo recorte. El wrapper escucha mouseleave para cerrarse cuando
 * el puntero se aleja también del panel.
 */
export function SidebarRailOverlay({ hover, pathname, onNavigate, onClose, panelRef }: SidebarRailOverlayProps) {
  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node
    },
    [panelRef]
  )

  if (typeof document === 'undefined') return null

  return createPortal(
    <div ref={setPanelRef} data-testid="sidebar-rail-overlay" onMouseLeave={onClose}>
      <SidebarRailPanel hover={hover} pathname={pathname} onNavigate={onNavigate} />
    </div>,
    document.body
  )
}
